import { useState, useEffect, useRef, useCallback } from 'react'
import { data, Form, useActionData, useNavigation, useSearchParams } from 'react-router'
import { z } from 'zod'
import { parseWithZod } from '@conform-to/zod/v4'
import { createLead } from '#app/routes/dashboard/leads/__lead.server.ts'
import { type SeoProps } from '#app/seo/seo.tsx'
import { type Route } from './+types/index.ts'

export const handle = {
	seo({ origin, canonical }: { origin: string; canonical: string }): SeoProps {
		return {
			title: 'Summit Roofing & Exteriors — Fort Worth\'s Most Trusted Roofing Team',
			description:
				'Storm damage repairs, full replacements, and exterior upgrades — backed by a Fort Worth team that shows up when they say they will. Free estimates.',
			canonical,
			openGraph: {
				title: 'Summit Roofing & Exteriors — Fort Worth\'s Most Trusted Roofing Team',
				description:
					'Storm damage repairs, full replacements, and exterior upgrades. Licensed & insured. 4.9★ with 127 reviews. Get your free estimate today.',
				type: 'website',
				url: canonical,
			},
			twitter: {
				card: 'summary_large_image',
				title: 'Summit Roofing & Exteriors — Fort Worth\'s Most Trusted Roofing Team',
				description:
					'Storm damage repairs, full replacements, and exterior upgrades. Get your free estimate today.',
			},
		}
	},
}

/* -------------------------------- SCHEMA / ACTION ---------------------- */

const LeadSchema = z.object({
	firstName: z.string().min(1, 'Required'),
	lastName: z.string().min(1, 'Required'),
	email: z.string().email('Enter a valid email'),
	phone: z.string().min(10, 'Enter a valid phone').optional(),
	address: z.string().optional(),
	city: z.string().optional(),
	state: z.string().optional(),
	zip: z.string().optional(),
	serviceRequested: z.string().min(1, 'Required'),
	source: z.string().optional(),
	notes: z.string().optional(),
	contactMethod: z.string().optional(),
})

export async function action({ request }: Route.ActionArgs) {
	const formData = await request.formData()
	const submission = parseWithZod(formData, { schema: LeadSchema })

	if (submission.status !== 'success') {
		return data(submission.reply(), { status: 400 })
	}

	const url = new URL(request.url)
	const attribution = {
		sourceUrl: request.headers.get('referer') ?? url.toString(),
		referrer: request.headers.get('referer') ?? undefined,
		utmSource: url.searchParams.get('utm_source') ?? undefined,
		utmMedium: url.searchParams.get('utm_medium') ?? undefined,
		utmCampaign: url.searchParams.get('utm_campaign') ?? undefined,
		utmTerm: url.searchParams.get('utm_term') ?? undefined,
		utmContent: url.searchParams.get('utm_content') ?? undefined,
		userAgent: request.headers.get('user-agent') ?? undefined,
		ipAddress: request.headers.get('x-forwarded-for') ?? undefined,
	}

	const result = await createLead({
		firstName: submission.value.firstName,
		lastName: submission.value.lastName,
		email: submission.value.email,
		phone: submission.value.phone,
		serviceRequested: submission.value.serviceRequested,
		notes: submission.value.notes,
		address: submission.value.address,
		city: submission.value.city,
		state: submission.value.state,
		zip: submission.value.zip,
		attribution,
	})

	if (!result.success) {
		return data(
			submission.reply({
				formErrors: [result.error ?? 'Failed to submit request'],
			}),
			{ status: 500 },
		)
	}

	return data({
		...submission.reply({ resetForm: true }),
		success: true,
	})
}

/* -------------------------------- DATA --------------------------------- */

const SERVICES = [
	{
		title: 'Storm damage & insurance claims',
		blurb: 'Hail hit? We handle the inspection, documentation, and insurance coordination so you don\u2019t have to.',
		detail:
			'Our field team documents every shingle, flashing, and vent with photos and adjuster-ready reports. We work directly with your carrier, meet the adjuster on-site, and make sure your claim covers everything it should \u2014 start to finish.',
		stat: '$2.1M+ in claims recovered in 2025',
	},
	{
		title: 'Roof replacement',
		blurb: 'Full tear-off and replacement with premium materials \u2014 architectural shingles, standing seam metal, tile, or slate.',
		detail:
			'Every replacement starts with a structural deck inspection, ice-and-water shield at every penetration, and synthetic underlayment. Finished in 1\u20133 days for most homes, with a 10-year workmanship warranty on top of the manufacturer\u2019s.',
		stat: '1,200+ roofs installed',
	},
	{
		title: 'Repairs & maintenance',
		blurb: 'Leaks, flashing, missing shingles. Fast response, honest assessment, quality repair.',
		detail:
			'We offer same-week service for active leaks and a flat-rate diagnostic fee credited toward any repair. If your roof is younger than 8 years and the repair is under $500, we\u2019ll tell you \u2014 not try to sell you a replacement.',
		stat: 'Avg response: 3.5 hours',
	},
	{
		title: 'Siding, gutters & exteriors',
		blurb: 'Complete exterior upgrades \u2014 siding, fascia, soffit, gutters, and painting.',
		detail:
			'James Hardie, LP SmartSide, and engineered wood siding. Seamless 6\u201d gutters with leaf protection. Full exterior paint with 10-year finish warranty. One crew, one point of contact, one punch list.',
		stat: '5-year exterior guarantee',
	},
]

const REVIEWS = [
	{
		quote: 'Summit replaced our entire roof after the April hailstorm. They handled the insurance claim, kept us updated every step of the way, and the crew was done in two days. Incredible experience.',
		name: 'Sarah M.',
		where: 'Southlake, TX',
		src: 'Google Review',
	},
	{
		quote: 'I got three estimates. Summit wasn\u2019t the cheapest, but they were the only ones who explained what needed to happen and actually showed up when they said they would. That\u2019s worth everything.',
		name: 'David R.',
		where: 'Keller, TX',
		src: 'Verified Customer',
	},
	{
		quote: 'Fast, professional, zero drama. They fixed a leak that two other companies couldn\u2019t figure out. Highly recommend.',
		name: 'Priya K.',
		where: 'Westover Hills',
		src: 'Google Review',
	},
]

const PROJECTS = [
	{
		label: 'Roof replacement',
		title: 'Full architectural shingle replacement',
		meta: 'Arlington \u00b7 42 squares \u00b7 2-day install',
		tone: 'warm' as const,
	},
	{
		label: 'Standing seam metal',
		title: 'Custom 24-gauge metal, matte bronze',
		meta: 'Westover Hills \u00b7 32 squares \u00b7 Color-matched',
		tone: 'dark' as const,
	},
	{
		label: 'Full exterior',
		title: 'Siding, gutters & exterior paint',
		meta: 'Southlake \u00b7 James Hardie \u00b7 3-week build',
		tone: 'warm' as const,
	},
]

const WHY = [
	{
		kicker: 'Fixed-price estimates',
		title: 'Transparent pricing.',
		body: 'You\u2019ll see a detailed estimate before we touch a single shingle. No hidden fees. No change orders. The price we quote is the price you pay.',
	},
	{
		kicker: 'Real-time updates',
		title: 'A project you can actually track.',
		body: 'Track your project from your phone. Know when your crew arrives, when materials are delivered, and when the job is done \u2014 without calling the office.',
	},
	{
		kicker: 'Local, not a franchise',
		title: 'We live here. We work here.',
		body: 'When you call Summit, you get a Fort Worth team that stands behind their work \u2014 not a franchise 800 number routing you to a call center three states away.',
	},
	{
		kicker: 'Insurance, handled',
		title: 'We talk to your adjuster.',
		body: 'Storm damage? We document everything, work directly with your adjuster, and make sure your claim covers what it should. You focus on your family.',
	},
]

/* -------------------------------- HOOKS -------------------------------- */

function useCountUp(target: number, active: boolean) {
	const [n, setN] = useState(0)
	useEffect(() => {
		if (!active) return
		let raf: number
		const start = performance.now()
		const dur = 1600
		const step = (t: number) => {
			const p = Math.min(1, (t - start) / dur)
			const eased = 1 - Math.pow(1 - p, 3)
			setN(Math.floor(target * eased))
			if (p < 1) raf = requestAnimationFrame(step)
			else setN(target)
		}
		raf = requestAnimationFrame(step)
		return () => cancelAnimationFrame(raf)
	}, [active, target])
	return n
}

/* ------------------------------ COMPONENTS ----------------------------- */

function SectionLabel({ n, label }: { n: string; label: string }) {
	return (
		<div className="ds-reveal mb-6 flex items-center gap-4">
			<span
				className="font-mono text-[11px] tracking-widest"
				style={{ color: 'var(--ds-accent)' }}
			>
				{n}
			</span>
			<span
				className="h-px w-10"
				style={{ background: 'rgba(168, 162, 158, 0.6)' }}
			/>
			<span
				className="text-[11px] font-medium uppercase tracking-[0.24em]"
				style={{ color: '#57534e' }}
			>
				{label}
			</span>
		</div>
	)
}

function TrustBadge({ top, bottom }: { top: string; bottom: string }) {
	return (
		<div>
			<div
				className="ds-font-display text-[22px] leading-none md:text-[26px]"
				style={{ color: '#f5f1ea' }}
			>
				{top}
			</div>
			<div
				className="mt-2 text-[11px] uppercase tracking-[0.18em]"
				style={{ color: 'rgba(245, 241, 234, 0.55)' }}
			>
				{bottom}
			</div>
		</div>
	)
}

/* ------------------------------ HERO ----------------------------------- */

function Hero() {
	return (
		<section id="top" className="relative overflow-hidden pt-[72px]">
			<div className="ds-hero-mesh ds-grain relative">
				<div className="relative mx-auto max-w-[1360px] px-5 pt-16 pb-20 md:px-10 md:pt-28 md:pb-36">
					<div className="grid items-end gap-8 md:grid-cols-12">
						<div className="md:col-span-8">
							<div className="mb-6 flex items-center gap-3 md:mb-10">
								<span
									className="h-px w-10"
									style={{
										background:
											'rgba(245, 241, 234, 0.5)',
									}}
								/>
								<span
									className="text-[11px] font-medium uppercase tracking-[0.28em]"
									style={{
										color: 'rgba(245, 241, 234, 0.7)',
									}}
								>
									Fort Worth &middot; Since 2019
								</span>
							</div>
							<h1
								className="ds-font-display text-[56px] leading-[0.92] tracking-[-0.03em] md:text-[120px] lg:text-[148px]"
								style={{ color: '#f5f1ea' }}
							>
								Your roof,
								<br />
								<span
									className="italic"
									style={{
										fontFamily:
											"var(--ds-font-display), 'Instrument Serif', serif",
									}}
								>
									done right.
								</span>
							</h1>
							<p
								className="mt-7 max-w-[560px] text-[16px] leading-relaxed md:mt-10 md:text-[19px]"
								style={{
									color: 'rgba(235, 228, 214, 0.75)',
								}}
							>
								Storm damage repairs, full replacements, and
								exterior upgrades &mdash; backed by a team that
								shows up when they say they will.
							</p>
							<div className="mt-8 flex flex-wrap items-center gap-4 md:mt-10">
								<a
									href="#estimate"
									className="ds-btn-primary inline-flex items-center gap-2 rounded-sm px-7 py-4 text-[14px] font-semibold tracking-wide"
								>
									Get your free estimate
									<svg
										width="14"
										height="10"
										viewBox="0 0 14 10"
										fill="none"
									>
										<path
											d="M1 5H13 M9 1L13 5L9 9"
											stroke="currentColor"
											strokeWidth="1.5"
											strokeLinecap="round"
											strokeLinejoin="round"
										/>
									</svg>
								</a>
								<a
									href="#work"
									className="ds-link-underline inline-flex items-center gap-2 text-[14px] font-medium"
									style={{ color: '#f5f1ea' }}
								>
									See our work
								</a>
							</div>
						</div>

						<div className="md:col-span-4 md:pb-6">
							<div
								className="grid grid-cols-2 gap-6 border-t pt-8 md:gap-8 md:border-0 md:pt-0"
								style={{
									color: 'rgba(245, 241, 234, 0.9)',
									borderColor:
										'rgba(245, 241, 234, 0.15)',
								}}
							>
								<TrustBadge
									top="Licensed"
									bottom="& Insured"
								/>
								<TrustBadge
									top="4.9 ★"
									bottom="127 reviews"
								/>
								<TrustBadge
									top="DFW Local"
									bottom="Since 2019"
								/>
								<TrustBadge
									top="0% APR"
									bottom="Financing"
								/>
							</div>
						</div>
					</div>
				</div>

				<div
					className="absolute bottom-6 left-1/2 flex -translate-x-1/2 flex-col items-center gap-2 text-[10px] uppercase tracking-[0.3em]"
					style={{ color: 'rgba(245, 241, 234, 0.4)' }}
				>
					<span>Scroll</span>
					<span
						className="h-8 w-px"
						style={{ background: 'rgba(245, 241, 234, 0.3)' }}
					/>
				</div>
			</div>
		</section>
	)
}

/* ------------------------------ SERVICES ------------------------------- */

function ServiceRow({
	n,
	service,
	open,
	onToggle,
}: {
	n: number
	service: (typeof SERVICES)[0]
	open: boolean
	onToggle: () => void
}) {
	return (
		<div
			className="ds-reveal border-b"
			style={{ borderColor: 'rgba(28, 25, 23, 0.15)' }}
		>
			<button
				onClick={onToggle}
				className="group flex w-full items-start gap-6 py-7 text-left md:items-center md:gap-10 md:py-10"
			>
				<span
					className="w-8 shrink-0 pt-2 font-mono text-[11px] md:pt-0"
					style={{ color: '#78716c' }}
				>
					0{n + 1}
				</span>
				<div className="min-w-0 flex-1">
					<h3
						className="ds-font-display text-[26px] leading-[1.05] transition-colors duration-300 group-hover:text-[var(--ds-accent)] md:text-[40px]"
						style={{ color: '#1c1917' }}
					>
						{service.title}
					</h3>
					<p
						className="mt-2 max-w-[700px] text-[14.5px] leading-relaxed md:mt-3 md:text-[15.5px]"
						style={{ color: '#57534e' }}
					>
						{service.blurb}
					</p>
				</div>
				<span
					className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full border transition-transform duration-400 md:h-12 md:w-12 ${open ? 'rotate-45' : ''}`}
					style={{
						borderColor: open
							? '#1c1917'
							: 'rgba(28, 25, 23, 0.25)',
						background: open ? '#1c1917' : 'transparent',
						color: open ? '#f5f1ea' : '#292524',
					}}
				>
					<svg width="14" height="14" viewBox="0 0 14 14">
						<path
							d="M7 1V13 M1 7H13"
							stroke="currentColor"
							strokeWidth="1.5"
							strokeLinecap="round"
						/>
					</svg>
				</span>
			</button>
			<div className={`ds-expand-grid ${open ? 'open' : ''}`}>
				<div className="ds-expand-inner">
					<div className="grid gap-8 pb-10 pl-14 md:grid-cols-12 md:pb-14 md:pl-[72px]">
						<p
							className="max-w-[620px] text-[15px] leading-[1.65] md:col-span-7 md:col-start-1 md:text-[17px]"
							style={{ color: '#44403c' }}
						>
							{service.detail}
						</p>
						<div className="flex items-end md:col-span-4 md:col-start-9 md:justify-end">
							<div
								className="font-mono text-[11px] uppercase tracking-wider"
								style={{
									color: 'var(--ds-accent)',
								}}
							>
								{service.stat}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

function Services() {
	const [openIdx, setOpenIdx] = useState(0)
	return (
		<section id="services" className="py-24 md:py-36">
			<div className="mx-auto max-w-[1360px] px-5 md:px-10">
				<SectionLabel n="01" label="What we do" />
				<div className="mb-14 grid items-end gap-10 md:grid-cols-12 md:gap-16">
					<h2
						className="ds-reveal ds-font-display text-[44px] leading-[0.98] tracking-tight md:col-span-8 md:text-[72px]"
						style={{ color: '#1c1917' }}
					>
						Four services.
						<br />
						<span className="italic" style={{ color: '#57534e' }}>
							One standard.
						</span>
					</h2>
					<p
						className="ds-reveal text-[15.5px] leading-relaxed md:col-span-4"
						style={{ color: '#57534e' }}
					>
						We do exteriors. That&apos;s the whole business.
						It&apos;s why we&apos;re better at it than the guys who
						also do kitchens, decks, and fences.
					</p>
				</div>

				<div
					className="border-t"
					style={{ borderColor: 'rgba(28, 25, 23, 0.15)' }}
				>
					{SERVICES.map((s, i) => (
						<ServiceRow
							key={i}
							n={i}
							service={s}
							open={openIdx === i}
							onToggle={() =>
								setOpenIdx(openIdx === i ? -1 : i)
							}
						/>
					))}
				</div>
			</div>
		</section>
	)
}

/* -------------------------------- STATS -------------------------------- */

function Stat({ label, value }: { label: string; value: string }) {
	return (
		<div
			className="border-r px-2 py-10 text-center last:border-r-0 md:px-6 md:py-14 md:text-left"
			style={{ borderColor: 'rgba(28, 25, 23, 0.1)' }}
		>
			<div
				className="ds-font-display text-[44px] leading-none tabular-nums md:text-[64px]"
				style={{ color: '#1c1917' }}
			>
				{value}
			</div>
			<div
				className="mt-3 text-[11px] font-medium uppercase tracking-[0.22em]"
				style={{ color: '#78716c' }}
			>
				{label}
			</div>
		</div>
	)
}

function Stats() {
	const [active, setActive] = useState(false)
	const ref = useRef<HTMLElement>(null)
	useEffect(() => {
		const el = ref.current
		if (!el) return
		const io = new IntersectionObserver(
			(e) => e[0]?.isIntersecting && setActive(true),
			{ threshold: 0.4 },
		)
		io.observe(el)
		return () => io.disconnect()
	}, [])
	const a = useCountUp(1200, active)
	const c = useCountUp(127, active)
	const d = useCountUp(98, active)
	return (
		<section
			ref={ref}
			className="border-y"
			style={{
				borderColor: 'rgba(28, 25, 23, 0.15)',
				background: '#faf7f2',
			}}
		>
			<div className="mx-auto grid max-w-[1360px] grid-cols-2 px-5 md:grid-cols-4 md:px-10">
				<Stat
					label="Roofs installed"
					value={active ? a.toLocaleString() + '+' : '0'}
				/>
				<Stat
					label="Google rating"
					value={active ? '4.9★' : '0.0★'}
				/>
				<Stat
					label="Verified reviews"
					value={active ? c.toLocaleString() : '0'}
				/>
				<Stat
					label="Insurance-win rate"
					value={active ? d + '%' : '0%'}
				/>
			</div>
		</section>
	)
}

/* ------------------------------- REVIEWS ------------------------------- */

function ReviewCard({
	quote,
	name,
	where,
	src,
	n,
}: (typeof REVIEWS)[0] & { n: number }) {
	return (
		<figure
			className="ds-reveal flex h-full flex-col gap-6 border p-7 md:p-9"
			style={{
				background: '#f5f1ea',
				borderColor: 'rgba(28, 25, 23, 0.1)',
				transitionDelay: n * 80 + 'ms',
			}}
		>
			<div className="flex items-center justify-between">
				<div
					className="flex gap-0.5"
					style={{ color: 'var(--ds-accent)' }}
				>
					{'★★★★★'.split('').map((c, i) => (
						<span key={i} className="text-sm">
							{c}
						</span>
					))}
				</div>
				<span
					className="font-mono text-[10px] uppercase tracking-[0.18em]"
					style={{ color: '#78716c' }}
				>
					{src}
				</span>
			</div>
			<blockquote
				className="ds-font-display flex-1 text-[22px] leading-[1.25] md:text-[26px]"
				style={{ color: '#1c1917' }}
			>
				&ldquo;{quote}&rdquo;
			</blockquote>
			<figcaption
				className="flex items-center justify-between border-t pt-5"
				style={{ borderColor: 'rgba(28, 25, 23, 0.1)' }}
			>
				<div>
					<div
						className="text-sm font-semibold"
						style={{ color: '#1c1917' }}
					>
						{name}
					</div>
					<div
						className="mt-0.5 text-[12px]"
						style={{ color: '#78716c' }}
					>
						{where}
					</div>
				</div>
				<div
					className="ds-font-display flex h-10 w-10 items-center justify-center rounded-full text-sm"
					style={{ background: '#1c1917', color: '#f5f1ea' }}
				>
					{name
						.split(' ')
						.map((s) => s[0])
						.join('')}
				</div>
			</figcaption>
		</figure>
	)
}

function ReviewsSection() {
	return (
		<section
			id="reviews"
			className="py-24 md:py-36"
			style={{ background: '#faf7f2' }}
		>
			<div className="mx-auto max-w-[1360px] px-5 md:px-10">
				<SectionLabel n="02" label="The word on the street" />
				<div className="mb-14 flex flex-col gap-6 md:mb-20 md:flex-row md:items-end md:justify-between">
					<h2
						className="ds-reveal ds-font-display text-[44px] leading-[0.98] tracking-tight md:text-[72px]"
						style={{ color: '#1c1917' }}
					>
						Don&apos;t take{' '}
						<span className="italic" style={{ color: '#57534e' }}>
							our
						</span>{' '}
						word for it.
					</h2>
					<div className="ds-reveal flex items-center gap-5">
						<div className="flex flex-col">
							<div
								className="flex items-center gap-1 text-lg"
								style={{ color: 'var(--ds-accent)' }}
							>
								{'★★★★★'.split('').map((c, i) => (
									<span key={i}>{c}</span>
								))}
							</div>
							<div
								className="mt-2 font-mono text-[11px] uppercase tracking-wider"
								style={{ color: '#78716c' }}
							>
								4.9 &middot; 127 Google reviews
							</div>
						</div>
					</div>
				</div>

				<div className="grid gap-6 md:grid-cols-3">
					{REVIEWS.map((r, i) => (
						<ReviewCard key={i} {...r} n={i} />
					))}
				</div>
			</div>
		</section>
	)
}

/* ------------------------------ PORTFOLIO ------------------------------ */

function ProjectIllustration({ tone }: { tone: string }) {
	const stroke = tone === 'warm' ? '#1c1917' : '#faf7f2'
	return (
		<svg
			viewBox="0 0 400 300"
			className="absolute inset-0 h-full w-full"
			opacity="0.85"
		>
			<path
				d="M60 220 L200 80 L340 220"
				fill="none"
				stroke={stroke}
				strokeWidth="1.5"
			/>
			<path
				d="M60 220 L60 270 L340 270 L340 220"
				fill="none"
				stroke={stroke}
				strokeWidth="1.5"
			/>
			<path
				d="M100 200 L100 180 L140 180 L140 200"
				fill="none"
				stroke={stroke}
				strokeWidth="1.5"
			/>
			<path
				d="M260 200 L260 180 L300 180 L300 200"
				fill="none"
				stroke={stroke}
				strokeWidth="1.5"
			/>
			<path
				d="M180 270 L180 230 L220 230 L220 270"
				fill="none"
				stroke={stroke}
				strokeWidth="1.5"
			/>
		</svg>
	)
}

function ProjectCard({
	label,
	title,
	meta,
	tone,
	n,
}: (typeof PROJECTS)[0] & { n: number }) {
	const toneMap = {
		warm: {
			bg: '#c89052',
			ink: '#1c1917',
			stripe: 'rgba(28,25,23,0.08)',
		},
		cool: {
			bg: '#78716c',
			ink: '#faf7f2',
			stripe: 'rgba(250,247,242,0.1)',
		},
		dark: {
			bg: '#292524',
			ink: '#faf7f2',
			stripe: 'rgba(250,247,242,0.08)',
		},
	}
	const t = toneMap[tone] || toneMap.warm
	return (
		<article
			className="ds-reveal group cursor-pointer"
			style={{ transitionDelay: n * 90 + 'ms' }}
		>
			<div
				className="relative aspect-[4/3] overflow-hidden"
				style={{ background: t.bg }}
			>
				<div
					className="absolute inset-0"
					style={{
						backgroundImage: `repeating-linear-gradient(135deg, transparent 0 16px, ${t.stripe} 16px 32px)`,
					}}
				/>
				<ProjectIllustration tone={tone} />
				<div
					className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.2em] px-2.5 py-1"
					style={{ background: t.ink, color: t.bg }}
				>
					{label}
				</div>
				<div className="absolute right-4 bottom-4 flex h-10 w-10 items-center justify-center rounded-full bg-[#faf7f2]/95 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
					<svg
						width="14"
						height="10"
						viewBox="0 0 14 10"
						fill="none"
					>
						<path
							d="M1 5H13 M9 1L13 5L9 9"
							stroke="#1c1917"
							strokeWidth="1.5"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</svg>
				</div>
			</div>
			<div className="pt-5">
				<h3
					className="ds-font-display text-[22px] leading-[1.1] transition-colors duration-300 group-hover:text-[var(--ds-accent)] md:text-[24px]"
					style={{ color: '#1c1917' }}
				>
					{title}
				</h3>
				<p
					className="mt-2 font-mono text-[13px] tracking-wide"
					style={{ color: '#78716c' }}
				>
					{meta}
				</p>
			</div>
		</article>
	)
}

function BeforeScene() {
	return (
		<svg
			viewBox="0 0 1600 900"
			preserveAspectRatio="xMidYMid slice"
			className="h-full w-full"
		>
			<defs>
				<linearGradient id="bg-before" x1="0" x2="0" y1="0" y2="1">
					<stop offset="0" stopColor="#5a5348" />
					<stop offset="1" stopColor="#3a342d" />
				</linearGradient>
				<pattern
					id="damaged"
					width="40"
					height="18"
					patternUnits="userSpaceOnUse"
					patternTransform="rotate(10)"
				>
					<rect width="40" height="18" fill="#4a3f36" />
					<path d="M0 9 L40 9" stroke="#2f2820" strokeWidth="1" />
					<circle cx="10" cy="5" r="1.2" fill="#8a7968" />
					<circle cx="28" cy="13" r="1.8" fill="#6b5f54" />
					<path
						d="M22 2 L30 7"
						stroke="#2a231c"
						strokeWidth="0.6"
					/>
				</pattern>
			</defs>
			<rect width="1600" height="900" fill="url(#bg-before)" />
			<rect width="1600" height="300" fill="#7a6f5f" opacity="0.7" />
			<polygon
				points="200,520 800,220 1400,520 1400,800 200,800"
				fill="#6b5a48"
			/>
			<polygon points="200,520 800,220 1400,520" fill="url(#damaged)" />
			<g opacity="0.65">
				{Array.from({ length: 40 }).map((_, i) => (
					<circle
						key={i}
						cx={250 + ((i * 28) % 1100)}
						cy={
							260 +
							Math.sin(i * 1.3) * 90 +
							(i % 4) * 40
						}
						r={4 + (i % 3)}
						fill="#1c1917"
					/>
				))}
			</g>
			<polygon
				points="560,380 620,365 640,395 580,410"
				fill="#2a2620"
			/>
			<polygon
				points="880,320 950,305 970,335 900,350"
				fill="#2a2620"
			/>
			<polygon
				points="1100,430 1180,415 1200,450 1120,465"
				fill="#2a2620"
			/>
			<rect x="200" y="520" width="1200" height="280" fill="#8a7a65" />
			<rect x="400" y="580" width="180" height="220" fill="#2a2620" />
			<rect x="820" y="580" width="180" height="220" fill="#2a2620" />
			<rect x="1100" y="620" width="140" height="180" fill="#2a2620" />
		</svg>
	)
}

function AfterScene() {
	return (
		<svg
			viewBox="0 0 1600 900"
			preserveAspectRatio="xMidYMid slice"
			className="h-full w-full"
		>
			<defs>
				<linearGradient id="sky-after" x1="0" x2="0" y1="0" y2="1">
					<stop offset="0" stopColor="#d4a574" />
					<stop offset="0.5" stopColor="#e8c9a0" />
					<stop offset="1" stopColor="#f0dcc4" />
				</linearGradient>
				<pattern
					id="clean-shingles"
					width="32"
					height="14"
					patternUnits="userSpaceOnUse"
				>
					<rect width="32" height="14" fill="#2a2823" />
					<rect y="7" width="32" height="7" fill="#33302a" />
					<path
						d="M0 7 L32 7"
						stroke="#1a1815"
						strokeWidth="0.5"
					/>
					<path
						d="M16 0 L16 7 M0 7 L0 14 M32 7 L32 14"
						stroke="#1a1815"
						strokeWidth="0.4"
					/>
				</pattern>
			</defs>
			<rect width="1600" height="900" fill="url(#sky-after)" />
			<circle cx="1300" cy="180" r="120" fill="#f5d8a8" opacity="0.6" />
			<polygon
				points="200,520 800,220 1400,520 1400,800 200,800"
				fill="#a89886"
			/>
			<polygon
				points="200,520 800,220 1400,520"
				fill="url(#clean-shingles)"
			/>
			<polygon
				points="200,515 800,218 800,225 200,525"
				fill="#1a1815"
			/>
			<polygon
				points="800,218 1400,515 1400,525 800,225"
				fill="#1a1815"
			/>
			<rect x="200" y="520" width="1200" height="280" fill="#e9dcc4" />
			<rect x="200" y="515" width="1200" height="10" fill="#3a342d" />
			<rect x="400" y="580" width="180" height="220" fill="#2a3438" />
			<rect x="395" y="575" width="190" height="10" fill="#3a342d" />
			<rect x="820" y="580" width="180" height="220" fill="#2a3438" />
			<rect x="815" y="575" width="190" height="10" fill="#3a342d" />
			<rect x="1100" y="620" width="140" height="180" fill="#2a3438" />
			<rect x="1095" y="615" width="150" height="10" fill="#3a342d" />
			<polygon
				points="410,590 470,590 420,650"
				fill="#445558"
				opacity="0.6"
			/>
		</svg>
	)
}

function BeforeAfter() {
	const [pos, setPos] = useState(52)
	const ref = useRef<HTMLDivElement>(null)
	const dragRef = useRef(false)

	const onMove = useCallback((clientX: number) => {
		if (!ref.current) return
		const rect = ref.current.getBoundingClientRect()
		const p = Math.max(
			0,
			Math.min(100, ((clientX - rect.left) / rect.width) * 100),
		)
		setPos(p)
	}, [])

	useEffect(() => {
		const mm = (e: MouseEvent) =>
			dragRef.current && onMove(e.clientX)
		const tm = (e: TouchEvent) =>
			dragRef.current && e.touches[0] && onMove(e.touches[0].clientX)
		const up = () => {
			dragRef.current = false
			document.body.style.userSelect = ''
		}
		window.addEventListener('mousemove', mm)
		window.addEventListener('mouseup', up)
		window.addEventListener('touchmove', tm, { passive: true })
		window.addEventListener('touchend', up)
		return () => {
			window.removeEventListener('mousemove', mm)
			window.removeEventListener('mouseup', up)
			window.removeEventListener('touchmove', tm)
			window.removeEventListener('touchend', up)
		}
	}, [onMove])

	return (
		<div
			className="relative aspect-[16/9] select-none overflow-hidden md:aspect-[21/9]"
			style={{ background: '#1c1917' }}
			ref={ref}
		>
			<div className="absolute inset-0">
				<AfterScene />
				<div
					className="absolute top-5 right-5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em]"
					style={{ background: '#f5f1ea', color: '#1c1917' }}
				>
					After
				</div>
			</div>
			<div
				className="absolute inset-0"
				style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
			>
				<BeforeScene />
				<div
					className="absolute top-5 left-5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em]"
					style={{ background: '#1c1917', color: '#f5f1ea' }}
				>
					Before
				</div>
			</div>
			<div
				className="ds-ba-handle absolute top-0 bottom-0"
				style={{ left: `calc(${pos}% - 1px)` }}
				onMouseDown={() => {
					dragRef.current = true
					document.body.style.userSelect = 'none'
				}}
				onTouchStart={() => {
					dragRef.current = true
				}}
			>
				<div
					className="h-full w-px"
					style={{ background: 'rgba(245, 241, 234, 0.9)' }}
				/>
				<div className="absolute top-1/2 left-1/2 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-[#f5f1ea] shadow-xl">
					<svg
						width="20"
						height="12"
						viewBox="0 0 20 12"
						fill="none"
					>
						<path
							d="M7 1L2 6L7 11 M13 1L18 6L13 11"
							stroke="#1c1917"
							strokeWidth="1.8"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</svg>
				</div>
			</div>
			<div
				className="pointer-events-none absolute bottom-5 left-5 font-mono text-[10px] uppercase tracking-[0.2em]"
				style={{ color: 'rgba(245, 241, 234, 0.8)' }}
			>
				Featured &middot; Arlington, TX &mdash; Hail recovery
			</div>
		</div>
	)
}

function Portfolio() {
	return (
		<section id="work" className="py-24 md:py-36">
			<div className="mx-auto max-w-[1360px] px-5 md:px-10">
				<SectionLabel n="03" label="Recent work" />
				<div className="mb-14 grid items-end gap-10 md:mb-20 md:grid-cols-12 md:gap-16">
					<h2
						className="ds-reveal ds-font-display text-[44px] leading-[0.98] tracking-tight md:col-span-8 md:text-[72px]"
						style={{ color: '#1c1917' }}
					>
						Projects we&apos;re proud
						<br />
						to put{' '}
						<span className="italic" style={{ color: '#57534e' }}>
							our name on.
						</span>
					</h2>
					<div className="ds-reveal md:col-span-4 md:text-right">
						<a
							href="#estimate"
							className="ds-link-underline inline-flex items-center gap-2 text-[13px] font-medium"
							style={{ color: '#1c1917' }}
						>
							Start your project
							<svg
								width="14"
								height="10"
								viewBox="0 0 14 10"
								fill="none"
							>
								<path
									d="M1 5H13 M9 1L13 5L9 9"
									stroke="currentColor"
									strokeWidth="1.5"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</a>
					</div>
				</div>

				<div className="ds-reveal mb-6 md:mb-8">
					<BeforeAfter />
				</div>

				<div className="grid gap-6 md:grid-cols-3 md:gap-8">
					{PROJECTS.map((p, i) => (
						<ProjectCard key={i} {...p} n={i} />
					))}
				</div>
			</div>
		</section>
	)
}

/* ----------------------------- WHY SUMMIT ------------------------------ */

function WhySummit() {
	return (
		<section
			id="why"
			className="relative overflow-hidden py-24 md:py-36"
			style={{ background: '#1c1917', color: '#f5f1ea' }}
		>
			<div className="ds-grain absolute inset-0 opacity-40" />
			<div
				className="absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full opacity-20"
				style={{
					background:
						'radial-gradient(circle, var(--ds-accent), transparent 70%)',
				}}
			/>

			<div className="relative mx-auto max-w-[1360px] px-5 md:px-10">
				<div className="ds-reveal mb-6 flex items-center gap-4">
					<span
						className="font-mono text-[11px] tracking-widest"
						style={{ color: 'var(--ds-accent)' }}
					>
						04
					</span>
					<span
						className="h-px w-10"
						style={{
							background: 'rgba(245, 241, 234, 0.3)',
						}}
					/>
					<span
						className="text-[11px] font-medium uppercase tracking-[0.24em]"
						style={{
							color: 'rgba(245, 241, 234, 0.7)',
						}}
					>
						Why homeowners choose us
					</span>
				</div>
				<h2 className="ds-reveal ds-font-display max-w-[1100px] text-[44px] leading-[0.98] tracking-tight md:text-[88px]">
					We built the kind of roofing company
					<br />
					<span
						className="italic"
						style={{
							color: 'rgba(217, 207, 189, 0.75)',
						}}
					>
						we&apos;d want working on our own house.
					</span>
				</h2>

				<div className="mt-20 grid gap-x-16 gap-y-14 md:mt-28 md:grid-cols-2 md:gap-y-24">
					{WHY.map((w, i) => (
						<div
							key={i}
							className="ds-reveal"
							style={{ transitionDelay: i * 80 + 'ms' }}
						>
							<div className="mb-5 flex items-center gap-3">
								<span
									className="font-mono text-[11px] uppercase tracking-[0.2em]"
									style={{
										color: 'var(--ds-accent)',
									}}
								>
									0{i + 1}
								</span>
								<span
									className="font-mono text-[11px] uppercase tracking-[0.2em]"
									style={{
										color: 'rgba(245, 241, 234, 0.6)',
									}}
								>
									{w.kicker}
								</span>
							</div>
							<h3 className="ds-font-display mb-5 text-[32px] leading-[1.05] md:text-[44px]">
								{w.title}
							</h3>
							<p
								className="max-w-[480px] text-[15.5px] leading-[1.7]"
								style={{
									color: 'rgba(235, 228, 214, 0.7)',
								}}
							>
								{w.body}
							</p>
						</div>
					))}
				</div>
			</div>
		</section>
	)
}

/* ------------------------------ FORM HELPERS --------------------------- */

function FormField({
	label,
	error,
	span2,
	children,
}: {
	label: string
	error?: string
	span2?: boolean
	children: React.ReactNode
}) {
	return (
		<label className={`block ${span2 ? 'md:col-span-2' : ''}`}>
			<span className="ds-label">
				{label}
				{error ? (
					<span className="ml-2 text-[12px] font-medium normal-case tracking-normal text-[#b32b2b]">
						&middot; {error}
					</span>
				) : null}
			</span>
			{children}
		</label>
	)
}

function GroupHeader({
	title,
	sub,
	n,
}: {
	title: string
	sub: string
	n: string
}) {
	return (
		<div
			className="flex items-baseline gap-4 border-t pt-4 first:border-t-0 first:pt-0 md:col-span-2"
			style={{ borderColor: 'rgba(28, 25, 23, 0.1)' }}
		>
			<span
				className="pt-5 font-mono text-[11px] tracking-widest"
				style={{ color: 'var(--ds-accent)' }}
			>
				{n}
			</span>
			<div className="pt-5">
				<div
					className="ds-font-display text-[22px] leading-none"
					style={{ color: '#1c1917' }}
				>
					{title}
				</div>
				<div
					className="mt-1 text-[13px]"
					style={{ color: '#78716c' }}
				>
					{sub}
				</div>
			</div>
		</div>
	)
}

function FormSuccess() {
	return (
		<div
			className="ds-reveal in relative overflow-hidden p-10 text-center md:p-16"
			style={{ background: '#1c1917', color: '#f5f1ea' }}
		>
			<div
				className="absolute inset-0 opacity-30"
				style={{
					background:
						'radial-gradient(ellipse at top, var(--ds-accent), transparent 60%)',
				}}
			/>
			<div className="relative">
				<div
					className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full border-2"
					style={{ borderColor: 'var(--ds-accent)' }}
				>
					<svg
						width="22"
						height="18"
						viewBox="0 0 22 18"
						fill="none"
					>
						<path
							d="M2 9L8 15L20 2"
							stroke="var(--ds-accent)"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</svg>
				</div>
				<h3 className="ds-font-display mb-5 text-[36px] leading-[1] md:text-[54px]">
					We got it.
				</h3>
				<p
					className="mx-auto max-w-[520px] text-[16px] leading-relaxed"
					style={{ color: 'rgba(235, 228, 214, 0.75)' }}
				>
					A member of our team will reach out within 2 hours &mdash;
					usually faster. If your roof is actively leaking, call us
					now at{' '}
					<a
						className="underline underline-offset-4"
						href="tel:+18175550199"
						style={{
							color: 'var(--ds-accent)',
							textDecorationColor:
								'color-mix(in oklch, var(--ds-accent) 40%, transparent)',
						}}
					>
						(817) 555-0199
					</a>
					.
				</p>
				<div
					className="mt-8 inline-flex items-center gap-3 border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.24em]"
					style={{
						color: 'rgba(245, 241, 234, 0.5)',
						borderColor: 'rgba(245, 241, 234, 0.2)',
					}}
				>
					<span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />
					Lead received &middot; We'll be in touch
				</div>
			</div>
		</div>
	)
}

function InfoRow({
	icon,
	label,
	value,
	href,
}: {
	icon: 'phone' | 'clock' | 'map'
	label: string
	value: string
	href?: string
}) {
	const icons = {
		phone: (
			<svg width="14" height="14" viewBox="0 0 14 14" fill="none">
				<path
					d="M2 2.5C2 2 2.5 1.5 3 1.5H4.5C4.8 1.5 5 1.7 5 2L5.5 4C5.5 4.3 5.3 4.5 5 4.7L4 5.3C4.6 6.7 7.3 9.4 8.7 10L9.3 9C9.5 8.7 9.7 8.5 10 8.5L12 9C12.3 9 12.5 9.2 12.5 9.5V11C12.5 11.5 12 12 11.5 12C6 12 2 8 2 2.5Z"
					stroke="currentColor"
					strokeWidth="1.2"
				/>
			</svg>
		),
		clock: (
			<svg width="14" height="14" viewBox="0 0 14 14" fill="none">
				<circle
					cx="7"
					cy="7"
					r="5.5"
					stroke="currentColor"
					strokeWidth="1.2"
				/>
				<path
					d="M7 3.5V7L9 8.5"
					stroke="currentColor"
					strokeWidth="1.2"
					strokeLinecap="round"
				/>
			</svg>
		),
		map: (
			<svg width="14" height="14" viewBox="0 0 14 14" fill="none">
				<path
					d="M7 12.5S11.5 8.3 11.5 5.5A4.5 4.5 0 002.5 5.5C2.5 8.3 7 12.5 7 12.5Z"
					stroke="currentColor"
					strokeWidth="1.2"
				/>
				<circle
					cx="7"
					cy="5.5"
					r="1.5"
					stroke="currentColor"
					strokeWidth="1.2"
				/>
			</svg>
		),
	}

	const inner = (
		<div className="flex items-start gap-4">
			<div className="mt-1" style={{ color: 'var(--ds-accent)' }}>
				{icons[icon]}
			</div>
			<div>
				<div
					className="text-[11px] font-medium uppercase tracking-[0.22em]"
					style={{ color: '#78716c' }}
				>
					{label}
				</div>
				<div
					className="mt-1 text-[15px] font-medium"
					style={{ color: '#1c1917' }}
				>
					{value}
				</div>
			</div>
		</div>
	)
	return href ? (
		<a href={href} className="block">
			{inner}
		</a>
	) : (
		inner
	)
}

/* ------------------------------ LEAD SECTION --------------------------- */

function LeadSection() {
	const actionData = useActionData<typeof action>()
	const navigation = useNavigation()
	const isSubmitting = navigation.state === 'submitting'
	const [searchParams] = useSearchParams()

	const [values, setValues] = useState({
		firstName: '',
		lastName: '',
		email: '',
		phone: '',
		address: '',
		city: 'Fort Worth',
		state: 'TX',
		zip: '',
		serviceRequested: '',
		source: '',
		notes: '',
		contactMethod: 'Phone',
	})

	const [clientErrors, setClientErrors] = useState<Record<string, string>>(
		{},
	)

	const set = (k: string, v: string) => {
		setValues((x) => ({ ...x, [k]: v }))
		if (clientErrors[k]) setClientErrors((e) => ({ ...e, [k]: '' }))
	}

	const isSuccess =
		actionData && 'success' in actionData && actionData.success

	const serverErrors =
		actionData && 'error' in actionData
			? (actionData.error as Record<string, string[]>)
			: {}

	const getError = (field: string) => {
		if (clientErrors[field]) return clientErrors[field]
		const se = serverErrors[field]
		if (se && se.length > 0) return se[0]
		return undefined
	}

	return (
		<section
			id="estimate"
			className="relative overflow-hidden py-24 md:py-36"
			style={{ background: '#faf7f2' }}
		>
			<div
				className="absolute -left-40 top-40 h-[500px] w-[500px] rounded-full opacity-[0.06]"
				style={{
					background:
						'radial-gradient(circle, var(--ds-accent), transparent 70%)',
				}}
			/>
			<div className="relative mx-auto max-w-[1360px] px-5 md:px-10">
				<div className="grid gap-12 md:grid-cols-12 md:gap-20">
					<div className="md:col-span-5">
						<SectionLabel n="05" label="Start a project" />
						<h2
							className="ds-reveal ds-font-display text-[44px] leading-[0.98] tracking-tight md:text-[80px]"
							style={{ color: '#1c1917' }}
						>
							Get your
							<br />
							<span
								className="italic"
								style={{ color: '#57534e' }}
							>
								free estimate.
							</span>
						</h2>
						<p
							className="ds-reveal mt-7 max-w-[420px] text-[16px] leading-[1.65]"
							style={{ color: '#57534e' }}
						>
							Fill out the form and we&apos;ll get back to you
							within 2 hours &mdash; usually faster. No pressure,
							no sales scripts, just an honest assessment.
						</p>

						<div className="ds-reveal mt-10 space-y-5">
							<InfoRow
								icon="phone"
								label="Or just call"
								value="(817) 555-0199"
								href="tel:+18175550199"
							/>
							<InfoRow
								icon="clock"
								label="Typical response"
								value="Under 2 hours · 7 days a week"
							/>
							<InfoRow
								icon="map"
								label="Service area"
								value="All of DFW · Tarrant, Dallas, Denton, Collin"
							/>
						</div>

						<div
							className="ds-reveal mt-10 border-t pt-8"
							style={{
								borderColor: 'rgba(28, 25, 23, 0.1)',
							}}
						>
							<div
								className="flex gap-3"
								style={{
									color: 'var(--ds-accent)',
								}}
							>
								{'★★★★★'.split('').map((c, i) => (
									<span key={i}>{c}</span>
								))}
							</div>
							<p
								className="ds-font-display mt-3 max-w-[380px] text-[20px] italic leading-snug"
								style={{ color: '#292524' }}
							>
								&ldquo;Best contractor experience I&apos;ve
								ever had. Period.&rdquo;
							</p>
							<p
								className="mt-2 font-mono text-[12px] tracking-wide"
								style={{ color: '#78716c' }}
							>
								&mdash; J. Okafor, Southlake &middot; Google
								Review
							</p>
						</div>
					</div>

					<div className="md:col-span-7">
						<div
							className="border p-6 md:p-10"
							style={{
								background: '#f5f1ea',
								borderColor: 'rgba(28, 25, 23, 0.1)',
							}}
						>
							{isSuccess ? (
								<FormSuccess />
							) : (
								<Form
									method="post"
									className="ds-reveal grid gap-5 md:grid-cols-2 md:gap-6"
								>
									{/* Hidden attribution fields from URL search params */}
									{searchParams.get('utm_source') ? (
										<input
											type="hidden"
											name="utm_source"
											value={
												searchParams.get(
													'utm_source',
												) ?? ''
											}
										/>
									) : null}
									{searchParams.get('utm_medium') ? (
										<input
											type="hidden"
											name="utm_medium"
											value={
												searchParams.get(
													'utm_medium',
												) ?? ''
											}
										/>
									) : null}
									{searchParams.get('utm_campaign') ? (
										<input
											type="hidden"
											name="utm_campaign"
											value={
												searchParams.get(
													'utm_campaign',
												) ?? ''
											}
										/>
									) : null}
									{searchParams.get('utm_term') ? (
										<input
											type="hidden"
											name="utm_term"
											value={
												searchParams.get(
													'utm_term',
												) ?? ''
											}
										/>
									) : null}
									{searchParams.get('utm_content') ? (
										<input
											type="hidden"
											name="utm_content"
											value={
												searchParams.get(
													'utm_content',
												) ?? ''
											}
										/>
									) : null}

									<GroupHeader
										title="Contact info"
										sub="Who should we reach out to?"
										n="01"
									/>
									<FormField
										label="First name"
										error={getError('firstName')}
									>
										<input
											name="firstName"
											className={`ds-input ${getError('firstName') ? 'error' : ''}`}
											value={values.firstName}
											onChange={(e) =>
												set(
													'firstName',
													e.target.value,
												)
											}
										/>
									</FormField>
									<FormField
										label="Last name"
										error={getError('lastName')}
									>
										<input
											name="lastName"
											className={`ds-input ${getError('lastName') ? 'error' : ''}`}
											value={values.lastName}
											onChange={(e) =>
												set(
													'lastName',
													e.target.value,
												)
											}
										/>
									</FormField>
									<FormField
										label="Email"
										error={getError('email')}
									>
										<input
											name="email"
											type="email"
											className={`ds-input ${getError('email') ? 'error' : ''}`}
											value={values.email}
											onChange={(e) =>
												set('email', e.target.value)
											}
										/>
									</FormField>
									<FormField
										label="Phone"
										error={getError('phone')}
									>
										<input
											name="phone"
											type="tel"
											placeholder="(817) 555-0199"
											className={`ds-input ${getError('phone') ? 'error' : ''}`}
											value={values.phone}
											onChange={(e) =>
												set('phone', e.target.value)
											}
										/>
									</FormField>

									<GroupHeader
										title="Job site"
										sub="Where's the work happening?"
										n="02"
									/>
									<FormField
										label="Street address"
										error={getError('address')}
										span2
									>
										<input
											name="address"
											className={`ds-input ${getError('address') ? 'error' : ''}`}
											value={values.address}
											onChange={(e) =>
												set(
													'address',
													e.target.value,
												)
											}
										/>
									</FormField>
									<FormField
										label="City"
										error={getError('city')}
									>
										<input
											name="city"
											className={`ds-input ${getError('city') ? 'error' : ''}`}
											value={values.city}
											onChange={(e) =>
												set('city', e.target.value)
											}
										/>
									</FormField>
									<div className="grid grid-cols-2 gap-5 md:gap-6">
										<FormField
											label="State"
											error={getError('state')}
										>
											<select
												name="state"
												className={`ds-input ${getError('state') ? 'error' : ''}`}
												value={values.state}
												onChange={(e) =>
													set(
														'state',
														e.target.value,
													)
												}
											>
												<option value="TX">TX</option>
												<option value="OK">OK</option>
												<option value="LA">LA</option>
												<option value="AR">AR</option>
												<option value="NM">NM</option>
											</select>
										</FormField>
										<FormField
											label="ZIP"
											error={getError('zip')}
										>
											<input
												name="zip"
												className={`ds-input ${getError('zip') ? 'error' : ''}`}
												value={values.zip}
												onChange={(e) =>
													set(
														'zip',
														e.target.value,
													)
												}
											/>
										</FormField>
									</div>

									<GroupHeader
										title="Project details"
										sub="Tell us what's going on."
										n="03"
									/>
									<FormField
										label="Service needed"
										error={getError('serviceRequested')}
									>
										<select
											name="serviceRequested"
											className={`ds-input ${getError('serviceRequested') ? 'error' : ''}`}
											value={values.serviceRequested}
											onChange={(e) =>
												set(
													'serviceRequested',
													e.target.value,
												)
											}
										>
											<option value="">
												Select one&hellip;
											</option>
											<option>
												Storm damage / Insurance claim
											</option>
											<option>Roof replacement</option>
											<option>Roof repair</option>
											<option>
												Siding & exteriors
											</option>
											<option>Gutters</option>
											<option>Other</option>
										</select>
									</FormField>
									<FormField label="How did you hear about us?">
										<select
											name="source"
											className="ds-input"
											value={values.source}
											onChange={(e) =>
												set(
													'source',
													e.target.value,
												)
											}
										>
											<option value="">
												Select one&hellip;
											</option>
											<option>Google search</option>
											<option>Facebook</option>
											<option>Referral</option>
											<option>Yard sign</option>
											<option>Other</option>
										</select>
									</FormField>
									<FormField
										label="Describe your project"
										span2
									>
										<textarea
											name="notes"
											rows={4}
											className="ds-input resize-none"
											placeholder="Tell us what's going on — leaks, storm damage, age of roof, etc."
											value={values.notes}
											onChange={(e) =>
												set(
													'notes',
													e.target.value,
												)
											}
										/>
									</FormField>
									<FormField
										label="Preferred contact method"
										span2
									>
										<div className="flex flex-wrap gap-3">
											{['Phone', 'Text', 'Email'].map(
												(opt) => (
													<label
														key={opt}
														className="flex cursor-pointer items-center gap-3 rounded-sm border px-5 py-3 text-sm font-medium transition-all"
														style={{
															borderColor:
																values.contactMethod ===
																opt
																	? 'var(--ds-accent)'
																	: 'rgba(28, 25, 23, 0.2)',
															background:
																values.contactMethod ===
																opt
																	? 'color-mix(in oklch, var(--ds-accent) 5%, transparent)'
																	: 'transparent',
															color:
																values.contactMethod ===
																opt
																	? 'var(--ds-accent)'
																	: '#44403c',
														}}
													>
														<input
															type="radio"
															name="contactMethod"
															className="sr-only"
															checked={
																values.contactMethod ===
																opt
															}
															onChange={() =>
																set(
																	'contactMethod',
																	opt,
																)
															}
														/>
														<span
															className="h-3.5 w-3.5 rounded-full border-2"
															style={{
																borderColor:
																	values.contactMethod ===
																	opt
																		? 'var(--ds-accent)'
																		: '#78716c',
																background:
																	values.contactMethod ===
																	opt
																		? 'var(--ds-accent)'
																		: 'transparent',
															}}
														/>
														{opt}
													</label>
												),
											)}
										</div>
									</FormField>

									<div className="flex flex-col gap-5 pt-3 md:col-span-2 md:pt-4">
										<button
											type="submit"
											disabled={isSubmitting}
											className="ds-btn-primary flex w-full items-center justify-center gap-3 rounded-sm py-5 text-[15px] font-semibold tracking-wide disabled:opacity-70"
										>
											{isSubmitting ? (
												<>
													<span className="h-4 w-4 animate-spin rounded-full border-2 border-[#f5f1ea]/40 border-t-[#f5f1ea]" />
													Sending&hellip;
												</>
											) : (
												<>
													Request my free estimate
													<svg
														width="14"
														height="10"
														viewBox="0 0 14 10"
														fill="none"
													>
														<path
															d="M1 5H13 M9 1L13 5L9 9"
															stroke="currentColor"
															strokeWidth="1.5"
															strokeLinecap="round"
															strokeLinejoin="round"
														/>
													</svg>
												</>
											)}
										</button>
										<div
											className="flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-[12px]"
											style={{ color: '#78716c' }}
										>
											<span className="flex items-center gap-2">
												<svg
													width="12"
													height="14"
													viewBox="0 0 12 14"
													fill="none"
												>
													<rect
														x="1.5"
														y="6"
														width="9"
														height="7"
														rx="1"
														stroke="currentColor"
														strokeWidth="1.2"
													/>
													<path
														d="M3.5 6V4a2.5 2.5 0 015 0v2"
														stroke="currentColor"
														strokeWidth="1.2"
													/>
												</svg>
												Your information is private
												& never shared
											</span>
											<span className="flex items-center gap-2">
												<svg
													width="12"
													height="10"
													viewBox="0 0 12 10"
													fill="none"
												>
													<path
														d="M1 5L5 9L11 1"
														stroke="currentColor"
														strokeWidth="1.4"
														strokeLinecap="round"
														strokeLinejoin="round"
													/>
												</svg>
												No spam. No pressure. Just
												an honest estimate.
											</span>
										</div>
									</div>
								</Form>
							)}
						</div>
					</div>
				</div>
			</div>
		</section>
	)
}

/* --------------------------------- PAGE -------------------------------- */

export default function Index() {
	return (
		<>
			<Hero />
			<Services />
			<Stats />
			<ReviewsSection />
			<Portfolio />
			<WhySummit />
			<LeadSection />
		</>
	)
}
